<template>
  <do-month-picker
    :value="lookBack"
    is-icon
    month-picker-menu-class="-left-40"
    @click.stop
    @context:change="handleContextChange"
  >
    <template #icon-activator="{ toggleMenu }">
      <slot :toggle-menu="toggleMenu">
        <icon-base
          :icon="context ? IconEdit : IconWarning"
          :icon-color="context ? UTIL_COLORS.DEFAULT : UTIL_COLORS.WARNING"
          class="cursor-pointer"
          data-cy="set-look-back-context"
          @click.stop="toggleMenu"
        />
      </slot>
    </template>
  </do-month-picker>
</template>
<script setup lang="ts">
import { computed } from "vue";

import { DEFAULT_LOOKBACK_PERIOD, UTIL_COLORS } from "@/helpers/constants";
import { formatDateCustom } from "@/helpers/formatting";

import IconEdit from "@/components/icons/IconEdit.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import IconBase from "@/components/ui/IconBase.vue";
import DoMonthPicker from "@/views/profile/components/dataOrchestration/configPanel/DOMonthPicker.vue";

import type {
  IContextSetValueModel,
  IRelativeDateRange,
  ISpecificDateRange,
  LookbackValue,
  RelativeLookbackRange,
} from "@/models/orchestration";
import type { PropType } from "vue";

const emit = defineEmits<{
  (event: "apply", value: IContextSetValueModel): void;
}>();

const props = defineProps({
  context: {
    type: Object as PropType<IContextSetValueModel>,
    default: undefined,
  },
});

const lookBack = computed(() => {
  if (props.context) {
    if (props.context.type === "relative_lookback_range") {
      const { lookback_months_from, lookback_months_through } = props.context
        .value as RelativeLookbackRange;
      return [lookback_months_from, lookback_months_through];
    }
    if (props.context.type === "specific_date_range") {
      const { from, through } = props.context.value as ISpecificDateRange;
      return [from, through];
    } else if (props.context.type === "relative_date_range") {
      return [(props.context.value as IRelativeDateRange).lookback_months];
    }
  }
  return DEFAULT_LOOKBACK_PERIOD;
});

const getContext = (data: LookbackValue): IContextSetValueModel | undefined => {
  if (data.type === "relative_lookback_range") {
    if (data.lookback.length === 1) {
      return {
        type: "relative_lookback_range",
        value: {
          lookback_months_from: Number(data.lookback[0]),
          lookback_months_through: Number(data.lookback[0]),
        },
      };
    }

    return {
      type: "relative_lookback_range",
      value: {
        lookback_months_from: Number(data.lookback[0]),
        lookback_months_through: Number(data.lookback[1]),
      },
    };
  }
  if (data.lookback.length === 2) {
    return {
      type: "specific_date_range",
      value: {
        from: formatDateCustom(data.lookback[0] as string, "yyyy-MM-dd"),
        through: formatDateCustom(data.lookback[1] as string, "yyyy-MM-dd"),
      },
    };
  } else if (data.lookback.length === 1) {
    return {
      type: "relative_date_range",
      value: {
        lookback_months: Number(data.lookback[0]),
        include_month_to_date: data.hasMonthToDate,
      },
    };
  }
};
const handleContextChange = (data: LookbackValue) => {
  if (data.lookback[0] === null) {
    return;
  }
  const context = getContext(data);
  if (context) {
    emit("apply", context);
  }
};
</script>
